/** What is stuck and why (GET /api/diagnostics): failed / needs_human / blocked steps grouped by
 * (entity, step, normalised reason), queue backlog per pool, egress pool health per site. Copy/Download MD use the
 * server's twin (/api/export/diagnostics.md). Shown on the Jobs page. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { StatusPill } from "../../components/StatusPill";
import { copyText, exportUrl, fetchMarkdown } from "../../lib/md";
import { AppLink } from "../board/cells";
import { entityHref, entityLabel } from "../lib/entity";
import { Empty, QueryView, TableScroll } from "../lib/ui";

type Diag = Schemas["Diagnostics"];

/** 45s, 12m, 5h, 3d. */
export function fmtAge(s: number | null | undefined): string {
  if (s == null) return "–";
  if (s < 120) return `${s}s`;
  if (s < 7200) return `${Math.floor(s / 60)}m`;
  if (s < 172800) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export const diagnosticsKey = qk.list("diagnostics");

export function diagnosticsSummary(d: Diag): string {
  const t = d.totals;
  return `${t.failed ?? 0} failed, ${t.needs_human ?? 0} needs human, ${t.blocked ?? 0} blocked`;
}

function Examples({ entity, ids }: { entity: string; ids: number[] }) {
  return (
    <span className="ws7-inline" style={{ flexWrap: "wrap" }}>
      {ids.map((id) => {
        const href = entityHref(entity, id);
        return href ? <AppLink key={id} href={href}>{entityLabel(entity, id)}</AppLink> : <span key={id}>#{id}</span>;
      })}
    </span>
  );
}

export function DiagnosticsPanel() {
  const qc = useQueryClient();
  const announce = useAnnounce();
  const q = useQuery({ queryKey: diagnosticsKey, queryFn: () => unwrap(api.GET("/api/diagnostics")), refetchInterval: 60_000 });
  return (
    <div data-testid="diagnostics">
      <div className="ws7-toolbar">
        <button type="button" data-testid="diagnostics-refresh" onClick={async () => {
          await qc.invalidateQueries({ queryKey: diagnosticsKey });
          announce("Diagnostics refreshed");
        }}>
          Refresh diagnostics
        </button>
        <button type="button" data-testid="diagnostics-copy-md" onClick={async () => {
          try {
            await copyText(await fetchMarkdown("diagnostics", {}));
            announce("Diagnostics Markdown copied");
          } catch (e) {
            announce(`Copy failed: ${(e as Error).message}`, "error");
          }
        }}>
          Copy diagnostics MD
        </button>
        <a className="button" data-testid="diagnostics-download-md" href={exportUrl("diagnostics", {}, true)}>
          Download diagnostics MD
        </a>
        {q.data && <span className="ws7-muted" data-testid="diagnostics-summary">{diagnosticsSummary(q.data)}</span>}
      </div>
      <QueryView q={q} what="diagnostics">
        {(d) => (
          <>
            <h3>Stuck steps</h3>
            {d.stuck.length === 0 ? (
              <Empty>Nothing is stuck.</Empty>
            ) : (
              <TableScroll label="Stuck steps table">
                <table className="ws7-table" aria-label="Stuck steps" data-testid="diagnostics-stuck">
                  <thead>
                    <tr>
                      <th scope="col">State</th>
                      <th scope="col">Entity</th>
                      <th scope="col">Step</th>
                      <th scope="col" className="num">Count</th>
                      <th scope="col">Oldest</th>
                      <th scope="col">Reason</th>
                      <th scope="col">Examples</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.stuck.map((g, i) => (
                      <tr key={`${g.state}-${g.entity}-${g.step}-${g.reason}`} data-testid={`diagnostics-stuck-${i}`}>
                        <td><StatusPill state={g.state} /></td>
                        <td>{g.entity}</td>
                        <td className="ws7-mono">{g.step}</td>
                        <td className="num">{g.count}</td>
                        <td title={`since ${new Date(g.oldest_at * 1000).toISOString()}`}>{fmtAge(g.oldest_age_s)}</td>
                        <td className="ws7-mono" title={g.sample_message ?? undefined}>{g.reason}</td>
                        <td><Examples entity={g.entity} ids={g.example_ids} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
            <h3>Queues</h3>
            <TableScroll label="Queue backlog table">
              <table className="ws7-table" aria-label="Queue backlog" data-testid="diagnostics-pools">
                <thead>
                  <tr>
                    <th scope="col">Pool</th>
                    <th scope="col" className="num">Slots</th>
                    <th scope="col" className="num">Running</th>
                    <th scope="col" className="num">Queued</th>
                    <th scope="col" className="num">Waiting (backoff / deferred)</th>
                    <th scope="col">Oldest queued</th>
                    <th scope="col">Oldest running</th>
                  </tr>
                </thead>
                <tbody>
                  {d.pools.map((p) => (
                    <tr key={p.pool} data-testid={`diagnostics-pool-${p.pool}`}>
                      <th scope="row">{p.pool}</th>
                      <td className="num">{p.slots}</td>
                      <td className="num">{p.running}</td>
                      <td className="num">{p.queued}</td>
                      <td className="num">{p.deferred}</td>
                      <td>{fmtAge(p.oldest_queued_age_s)}</td>
                      <td>{fmtAge(p.oldest_running_age_s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <h3>Egress proxy pool</h3>
            {d.egress.length === 0 ? (
              <Empty>No proxy endpoints configured.</Empty>
            ) : (
              <TableScroll label="Egress pool table">
                <table className="ws7-table" aria-label="Egress pool" data-testid="diagnostics-egress">
                  <thead>
                    <tr>
                      <th scope="col">Site</th>
                      <th scope="col" className="num">Usable</th>
                      <th scope="col" className="num">Total</th>
                      <th scope="col" className="num">OK</th>
                      <th scope="col" className="num">Cooldown</th>
                      <th scope="col" className="num">Unknown</th>
                      <th scope="col" className="num">Sessions</th>
                      <th scope="col" className="num" title="Share of requests through this site's endpoints that worked">Success</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.egress.map((e) => (
                      <tr key={e.site} data-testid={`diagnostics-egress-${e.site}`}>
                        <th scope="row">{e.site}</th>
                        <td className={`num${e.total > 0 && e.usable === 0 ? " ws7-tone-bad" : ""}`}>{e.usable}</td>
                        <td className="num">{e.total}</td>
                        <td className="num">{e.ok}</td>
                        <td className="num">{e.cooldown}</td>
                        <td className="num">{e.unknown}</td>
                        <td className="num">{e.sessions}</td>
                        <td className="num" title={e.rotating ? "rotating provider: every try is a fresh exit IP" : undefined}>
                          {e.success_rate == null ? "–" : `${Math.round(e.success_rate * 100)}%`}
                          {e.rotating ? " ↻" : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}
